import React, { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { HELP_CATEGORIES, HELP_ARTICLES, articlesByCategory, findArticle } from "@/lib/helpArticles";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowLeft, BookOpen, ChevronRight, Clock, FileDown, Printer } from "lucide-react";
import PageHeader from "@/components/PageHeader";

export default function HelpCenter() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return HELP_ARTICLES;
        return HELP_ARTICLES.filter((a) =>
            a.title.toLowerCase().includes(q) ||
            a.summary.toLowerCase().includes(q) ||
            a.body.some((b) => (b.p || "").toLowerCase().includes(q) || (b.list || []).some((l) => l.toLowerCase().includes(q)))
        );
    }, [query]);

    if (slug) {
        const art = findArticle(slug);
        if (!art) {
            return (
                <div>
                    <button onClick={() => navigate("/app/help")} className="text-sm text-secondary-dark hover:text-secondary flex items-center gap-1.5 mb-4">
                        <ArrowLeft className="h-4 w-4" /> Back to Help Center
                    </button>
                    <p className="text-muted-foreground">Article not found.</p>
                </div>
            );
        }
        const Icon = art.icon;
        return (
            <div className="max-w-3xl mx-auto">
                <button onClick={() => navigate("/app/help")} className="text-sm text-secondary-dark hover:text-secondary flex items-center gap-1.5 mb-6" data-testid="help-back">
                    <ArrowLeft className="h-4 w-4" /> Back to Help Center
                </button>
                <div className="flex items-center gap-4 mb-6">
                    <div className="h-14 w-14 rounded-2xl bg-ocean-50 text-secondary grid place-items-center">
                        <Icon className="h-6 w-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <Badge className="bg-sand-200 text-ocean-700 border-0">{HELP_CATEGORIES.find((c) => c.id === art.category)?.title}</Badge>
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {art.readTime}</span>
                        </div>
                        <h1 className="mt-2 font-heading text-3xl sm:text-4xl text-ocean-700 tracking-tight">{art.title}</h1>
                        <p className="mt-1 text-muted-foreground">{art.summary}</p>
                    </div>
                </div>
                <article className="rounded-3xl bg-white border border-sand-200 shadow-card p-8 sm:p-10 space-y-5">
                    {art.body.map((block, i) => {
                        if (block.h) return <h3 key={i} className="font-heading text-xl text-ocean-700 pt-2">{block.h}</h3>;
                        if (block.p) return <p key={i} className="text-ocean-700/90 leading-relaxed">{block.p}</p>;
                        if (block.list) return (
                            <ul key={i} className="space-y-2">
                                {block.list.map((li, j) => (
                                    <li key={j} className="flex gap-3 text-ocean-700/90 leading-relaxed">
                                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-coral shrink-0" />
                                        <span>{li}</span>
                                    </li>
                                ))}
                            </ul>
                        );
                        return null;
                    })}
                </article>
                <RelatedArticles current={art} />
            </div>
        );
    }

    // Index view
    return (
        <div>
            <PageHeader subtitle="Workspace" title="Help Center" />

            {/* Downloadable user manual — full-length PDF + markdown source. */}
            <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-5 sm:p-6 mb-6 flex items-center gap-4 flex-wrap" data-testid="help-manual-cta">
                <div className="h-11 w-11 rounded-xl bg-secondary/10 text-secondary-dark grid place-items-center shrink-0">
                    <BookOpen className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-[240px]">
                    <div className="font-heading text-lg text-ocean-700">Complete user manual</div>
                    <div className="text-sm text-muted-foreground mt-0.5">Every feature in one place — searchable in the app, printable to PDF, or downloadable as Markdown.</div>
                </div>
                <div className="flex gap-2">
                    <Link to="/manual" target="_blank" rel="noopener noreferrer" data-testid="help-open-manual">
                        <button className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 bg-white hover:bg-sand-50 text-ocean-700 text-sm h-9 px-3">
                            <Printer className="h-4 w-4" /> Open &amp; Print
                        </button>
                    </Link>
                    <a href="/BlueLedger-User-Manual.md" download className="inline-flex items-center gap-1.5 rounded-xl bg-ocean-700 hover:bg-ocean-700/90 text-white text-sm h-9 px-3" data-testid="help-download-manual">
                        <FileDown className="h-4 w-4" /> Download .md
                    </a>
                </div>
            </div>

            <div className="rounded-3xl bg-deep-ocean text-white p-8 sm:p-10 shadow-card relative overflow-hidden mb-8">
                <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-secondary/25 blur-3xl" />
                <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-coral/20 blur-3xl" />
                <div className="relative max-w-2xl">
                    <BookOpen className="h-8 w-8 text-secondary-light" />
                    <h2 className="mt-4 font-heading text-4xl tracking-tight">How can we help you today?</h2>
                    <p className="mt-2 text-white/70">Short, practical walkthroughs for every section of BlueLedger.</p>
                    <div className="mt-6 relative">
                        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search articles — e.g. Cayman tax, reconcile, OCR receipt"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="h-12 pl-11 rounded-xl bg-white text-ocean-700 border-0"
                            data-testid="help-search"
                        />
                    </div>
                </div>
            </div>

            {query ? (
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-secondary-dark font-semibold mb-4">
                        {filtered.length} {filtered.length === 1 ? "result" : "results"}
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map((a) => <ArticleCard key={a.slug} article={a} />)}
                    </div>
                </div>
            ) : (
                HELP_CATEGORIES.map((cat) => (
                    <section key={cat.id} className="mb-10">
                        <h3 className="font-heading text-xl text-ocean-700 mb-4">{cat.title}</h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {articlesByCategory(cat.id).map((a) => <ArticleCard key={a.slug} article={a} />)}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
}

function ArticleCard({ article }) {
    const Icon = article.icon;
    return (
        <Link
            to={`/app/help/${article.slug}`}
            data-testid={`help-article-${article.slug}`}
            className="group rounded-3xl bg-white border border-sand-200 shadow-card p-5 hover:shadow-card-hover hover:border-secondary/30 transition-all flex flex-col"
        >
            <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-xl bg-ocean-50 text-secondary grid place-items-center">
                    <Icon className="h-5 w-5" />
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-secondary transition-colors mt-2" />
            </div>
            <h4 className="mt-4 font-heading text-lg text-ocean-700 leading-snug">{article.title}</h4>
            <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed line-clamp-2">{article.summary}</p>
            <div className="mt-4 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {article.readTime}
            </div>
        </Link>
    );
}

function RelatedArticles({ current }) {
    const related = HELP_ARTICLES.filter((a) => a.category === current.category && a.slug !== current.slug).slice(0, 3);
    if (related.length === 0) return null;
    return (
        <div className="mt-10">
            <h3 className="font-heading text-lg text-ocean-700 mb-4">Related articles</h3>
            <div className="grid sm:grid-cols-3 gap-4">
                {related.map((a) => <ArticleCard key={a.slug} article={a} />)}
            </div>
        </div>
    );
}
